<template>
  <v-dialog v-model="dialog" max-width="600" :retain-focus="false">
    <v-card>
      <v-card-title class="text-h5">Validating model parameters</v-card-title>
      <v-card-text>
        <v-sheet border rounded class="mb-4">
          <template v-for="(step, i) in steps" :key="step.index">
            <v-divider v-if="i > 0"></v-divider>
            <div class="d-flex align-start pa-3">
              <v-icon :color="iconColor(step)" class="mr-3 mt-1">
                {{ iconName(step) }}
              </v-icon>
              <div class="flex-grow-1">
                <div class="text-subtitle-1 font-weight-medium">
                  {{ step.title }}
                </div>
                <template v-if="vSteps > step.index">
                  <small
                    v-if="step.valid"
                    class="d-block mt-1 text-medium-emphasis"
                  >
                    {{ step.okMessage }}
                  </small>
                  <div v-else class="d-flex align-center mt-2">
                    <small class="text-medium-emphasis mr-4">
                      {{ step.errorMessage }}
                    </small>
                    <v-btn
                      size="small"
                      color="primary"
                      class="ml-auto flex-shrink-0"
                      :title="step.errorMessage"
                      @click="fixModelErrors(step.index)"
                    >
                      Fix
                    </v-btn>
                  </div>
                </template>
              </div>
            </div>
          </template>
        </v-sheet>

        <table class="validation-summary">
          <tbody>
            <tr>
              <th>Requested Date Range:</th>
              <td>
                {{ dateRangeBacking.startDateTime }} to
                {{ dateRangeBacking.endDateTime }}
              </td>
            </tr>
            <tr>
              <th>Weather Normalized Records Returned:</th>
              <td>{{ validationResultBacking.actualRecordCount }}</td>
            </tr>
            <tr>
              <th>First Energy Record:</th>
              <td>{{ validationResultBacking.energyStartDate }}</td>
            </tr>
            <tr>
              <th>Last Energy Record:</th>
              <td>{{ validationResultBacking.energyEndDate }}</td>
            </tr>
            <tr>
              <th>Energy Records Returned:</th>
              <td>{{ validationResultBacking.energyRecordCount }}</td>
            </tr>
            <tr>
              <th>Missing Energy Records in Range:</th>
              <td>{{ validationResultBacking.missingEnergyDataRecordCount }}</td>
            </tr>
            <tr>
              <th>First Weather Record:</th>
              <td>{{ validationResultBacking.weatherStartDate }}</td>
            </tr>
            <tr>
              <th>Last Weather Record:</th>
              <td>{{ validationResultBacking.weatherEndDate }}</td>
            </tr>
            <tr>
              <th>Weather Records Returned:</th>
              <td>{{ validationResultBacking.weatherRecordCount }}</td>
            </tr>
            <tr>
              <th>Missing Weather Records in Range:</th>
              <td>
                {{ validationResultBacking.missingWeatherDataRecordCount }}
              </td>
            </tr>
          </tbody>
        </table>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>

        <v-btn
          color="green-darken-1"
          variant="text"
          :disabled="!isValidModel"
          @click="dismissDialog"
        >
          Continue
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  components: {},

  props: {
    invalid: { type: Boolean, default: () => true },
    modelName: { type: String, default: () => null },
    factors: { type: Array, default: () => [] },
    dateRange: {},
    nameValidationResult: { type: Boolean, default: () => true },
    dataValidationResult: {},
  },
  watch: {
    dataValidationResult: function (newVal) {
      this.validationResultBacking = newVal ?? {};
    },
    dateRange: function (newVal) {
      this.dateRangeBacking = newVal ?? {};
    },
  },
  data() {
    return {
      dialog: false,
      vSteps: 0,
      validationResultBacking: {},
      dateRangeBacking: {},
      isValidModel: false,
      isValidModelName: false,
      isValidWeatherData: false,
      isValidEnergyData: false,
      isValidFactors: false,
      nameIndex: 1,
      energyIndex: 2,
      weatherIndex: 3,
      factorIndex: 4,
      interval: null,
    };
  },

  beforeUnmount() {
    // Stop the validation timer if the component is torn down mid-run (e.g. a
    // route change) so it can't fire after unmount or leak.
    this.stopValidator();
  },

  computed: {
    steps() {
      return [
        {
          index: this.nameIndex,
          title: 'Validating Model Name',
          valid: this.isValidModelName,
          okMessage: 'Model name is valid',
          errorMessage: 'Model name already exists.  Enter another model name',
        },
        {
          index: this.energyIndex,
          title: 'Validating Energy Data',
          valid: this.isValidEnergyData,
          okMessage: 'Energy Data is valid',
          errorMessage: this.dataValidationResult?.energyWarningMessage,
        },
        {
          index: this.weatherIndex,
          title: 'Validating Weather Data',
          valid: this.isValidWeatherData,
          okMessage: 'Weather Data for Date range is valid',
          errorMessage: this.dataValidationResult?.weatherWarningMessage,
        },
        {
          index: this.factorIndex,
          title: 'Validating Factor',
          valid: this.isValidFactors,
          okMessage: 'Factor is valid',
          errorMessage: 'Factor must be selected.',
        },
      ];
    },
  },

  methods: {
    iconColor(step) {
      if (this.vSteps <= step.index) return 'grey-lighten-1';
      return step.valid ? 'green' : 'warning';
    },

    iconName(step) {
      if (this.vSteps <= step.index) return 'mdi-circle-outline';
      return step.valid ? 'mdi-check-circle' : 'mdi-alert-circle';
    },

    validateModel() {
      this.isValidModel = false;
      this.isValidModelName = false;
      this.isValidEnergyData = false;
      this.isValidWeatherData = false;
      this.isValidFactors = false;
      this.vSteps = 0;
      this.dialog = true;
      this.startValidator();
    },

    startValidator() {
      this.stopValidator();
      this.interval = setInterval(() => {
        this.vSteps++;

        if (this.vSteps == this.nameIndex) {
          this.isValidModelName = this.nameValidationResult === true;
        }

        if (this.vSteps == this.energyIndex) {
          this.isValidEnergyData =
            (this.dataValidationResult?.energyWarningMessage?.length ?? 0) === 0;
        }

        if (this.vSteps == this.weatherIndex) {
          this.isValidWeatherData =
            (this.dataValidationResult?.weatherWarningMessage?.length ?? 0) ===
            0;
        }

        if (this.vSteps == this.factorIndex) {
          this.isValidFactors = this.factors.length > 0;
        }

        // Continue is gated on this, so every step must pass — otherwise a
        // "Fix" button would be showing while Continue stays enabled.
        this.isValidModel =
          this.isValidModelName &&
          this.isValidEnergyData &&
          this.isValidWeatherData &&
          this.isValidFactors;

        // Advance one step past the last item so every step's result row
        // renders, then stop. Without this the interval would run forever
        // and hang the dialog.
        if (this.vSteps > this.factorIndex) {
          this.vSteps = this.factorIndex + 1;
          this.stopValidator();
        }
      }, 1000);
    },

    stopValidator() {
      clearInterval(this.interval);
      this.interval = null;
    },

    fixModelErrors(item) {
      this.cancelDialog();

      const result = this.validationResultBacking;
      const newDateRange = { ...(this.dateRange ?? {}) };

      // Only steer the date range to the suggested window when the backend
      // actually returned dates — otherwise we'd blank out the range and the
      // next validation call would send null start/end times.
      if (item === this.energyIndex) {
        if (result.energyStartDate)
          newDateRange.startDateTime = result.energyStartDate;
        if (result.energyEndDate)
          newDateRange.endDateTime = result.energyEndDate;
      }
      if (item === this.weatherIndex) {
        if (result.weatherStartDate)
          newDateRange.startDateTime = result.weatherStartDate;
        if (result.weatherEndDate)
          newDateRange.endDateTime = result.weatherEndDate;
      }

      this.$emit('fixmodel', { item: item, newDateRange: newDateRange });
    },

    cancelDialog() {
      this.dialog = false;
      this.stopValidator();
      this.vSteps = 0;
    },

    dismissDialog() {
      this.cancelDialog();
      this.$emit('dismissdialog');
    },
  },
};
</script>

<style scoped>
.validation-summary {
  width: 100%;
  border-collapse: collapse;
}

.validation-summary th {
  text-align: left;
  font-weight: 600;
  white-space: nowrap;
  padding: 4px 12px 4px 0;
  vertical-align: top;
}

.validation-summary td {
  padding: 4px 0;
  word-break: break-word;
}
</style>
